/**
 * JWT и HTTP — ключи совпадают с iOS/Android витриной (wicoopStorage).
 */
const API_BASE = String(import.meta.env.VITE_API_BASE ?? "")
  .trim()
  .replace(/\/$/, "");

export const STORAGE_KEYS = {
  ACCESS: "wicoop_marketplace_access_v1",
  REFRESH: "wicoop_marketplace_refresh_v1",
};

export function getApiBase() {
  return API_BASE;
}

function getToken() {
  return localStorage.getItem(STORAGE_KEYS.ACCESS);
}

export function setTokens(access, refresh = null) {
  if (access) localStorage.setItem(STORAGE_KEYS.ACCESS, access);
  else localStorage.removeItem(STORAGE_KEYS.ACCESS);
  if (refresh) localStorage.setItem(STORAGE_KEYS.REFRESH, refresh);
  else if (refresh === null && !access) localStorage.removeItem(STORAGE_KEYS.REFRESH);
}

export function clearTokens() {
  localStorage.removeItem(STORAGE_KEYS.ACCESS);
  localStorage.removeItem(STORAGE_KEYS.REFRESH);
}

let refreshInFlight = null;

async function refreshAccessToken() {
  const refresh = localStorage.getItem(STORAGE_KEYS.REFRESH);
  if (!refresh) return null;
  const res = await fetch(`${API_BASE}/api/auth/token/refresh/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh }),
  });
  if (!res.ok) return null;
  const data = await res.json().catch(() => null);
  if (data?.access) {
    setTokens(data.access, data.refresh || refresh);
    return data.access;
  }
  return null;
}

function getFreshAccessOrRefresh() {
  if (!refreshInFlight) {
    refreshInFlight = refreshAccessToken().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

// Если refresh не помог (протухла и refresh-пара) — внутри Telegram можно молча
// переавторизоваться по initData. Раньше приложение считало «есть сохранённые
// токены» = «залогинен», даже когда они мертвы, и не переавторизовывалось —
// отсюда «Учетные данные не были предоставлены» на экранах профиля.
// Динамический import — чтобы не ловить циклический импорт (telegramWebAppAuth → http).
let reauthInFlight = null;
async function reauthViaTelegramIfPossible() {
  if (!reauthInFlight) {
    reauthInFlight = (async () => {
      try {
        const mod = await import("../lib/telegramWebAppAuth.js");
        if (!mod.isInsideTelegramWebApp?.()) return null;
        clearTokens(); // сбросить мёртвые токены, иначе re-auth короткозамкнётся
        const r = await mod.tryAuthenticateTelegramWebApp();
        return r?.ok ? getToken() : null;
      } catch {
        return null;
      } finally {
        reauthInFlight = null;
      }
    })();
  }
  return reauthInFlight;
}

export async function api(path, options = {}) {
  const body = options.body;
  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;
  const url = `${API_BASE}${path}`;

  const buildHeaders = (accessToken) => {
    const headers = {
      "X-Wicoop-Client": "mini_app",
      ...(body && typeof body === "string" ? { "Content-Type": "application/json" } : {}),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...options.headers,
    };
    if (isFormData) delete headers["Content-Type"];
    return headers;
  };

  let access = getToken();
  let headers = buildHeaders(access);
  let res = await fetch(url, { ...options, headers });

  if (
    res.status === 401 &&
    !String(path).includes("/auth/token/refresh/") &&
    !String(path).includes("/telegram-webapp/auth/")
  ) {
    // 1) обычный refresh (если есть что обновлять), 2) молчаливая переавторизация
    // через Telegram, если refresh не спас (сессия истекла целиком).
    let newAccess = access ? await getFreshAccessOrRefresh() : null;
    if (!newAccess) {
      newAccess = await reauthViaTelegramIfPossible();
    }
    if (newAccess) {
      headers = buildHeaders(newAccess);
      res = await fetch(url, { ...options, headers });
    }
  }

  return res;
}

export async function parseJson(res) {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { detail: text };
  }
}

function formatApiError(data) {
  if (!data || typeof data !== "object") return "Ошибка запроса";
  if (typeof data.detail === "string") return data.detail;
  if (Array.isArray(data.detail)) return data.detail.map(String).join(" ");
  const parts = [];
  for (const key of Object.keys(data)) {
    const v = data[key];
    if (Array.isArray(v)) parts.push(v.map(String).join(" "));
    else if (typeof v === "string") parts.push(v);
  }
  return parts.join(" ") || "Ошибка запроса";
}

export async function apiJson(path, options = {}) {
  const res = await api(path, options);
  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(formatApiError(data));
  }
  return data;
}
